import Swal from "sweetalert2";
import styled from 'styled-components';
import { Spinner } from 'react-bootstrap';
import DeleteIcon from 'mdi-react/DeleteIcon';
import DownloadIcon from "mdi-react/DownloadIcon";
import React, { useState, useEffect } from 'react';
import AdminInterface from '@/utils/AdminInterface';
import withReactContent from "sweetalert2-react-content";
import { paddingLeft, paddingRight } from '@/utils/directions';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import { colorAdditional, colorGreenHover, colorAccent } from '@/utils/palette';
import { Card, CardBody, CardTitleWrap, CardTitle, CardSubhead } from '@/shared/components/Card';

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 20px;
    width: 20px;
    fill: ${colorAdditional};
    transition: all 0.3s;
  }

  svg:hover {
    fill: ${colorGreenHover};
  }

  .orange-hover:hover {
    fill: #e9c886;
  }

  .red-hover:hover {
    fill: #e98686;
  }
`;

const TableSpinner = styled(Spinner)`
  color: ${colorAccent};
`;

const FilesList = () => {
    const [reload, setReload] = useState(true);
    const [showLoader, setShowLoader] = useState(true);
    const [filesList, setFilesList] = useState({ tableHeaderData: [], tableRowsData: [] });
    const tableConfig = {
        isEditable: false,
        isSortable: true,
        isResizable: false,
        withPagination: false,
        withSearchEngine: true,
        placeholder: 'Search...',
    };

    const handleDownload = (file) => {
        AdminInterface.instance.downloadFile(file.id).then((response) => {
            let filename = response.data['filename']
            const url = response.data['download_url']
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        }).catch((error) => {
            Swal.fire({
                title: 'Error',
                icon: 'error',
                html: `Downloading the mission centroids is not possible. ${error.response?.data?.detail || error.message || ''}`,
            });
        });return
    }

    const handleDelete = (file) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to delete the file ${file.file_name}?`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                AdminInterface.instance.deleteFile(file.id).then(() => {
                    withReactContent(Swal).fire({
                        icon: 'success',
                        title: "Success",
                        html: "File is deleted!",
                    });
                    setReload(true);
                }).catch((error) => {
                    withReactContent(Swal).fire({
                        icon: 'error',
                        title: "Error",
                        html: error.response.data?.detail,
                    });
                })
            }
        })
    }

    const GetFiles = () => {
        setShowLoader(true);
        const columns = [
            {
                accessor: 'id',
                Header: 'Id',
            },
            {
                accessor: 'user_id',
                Header: 'User',
            },
            {
                accessor: 'filename',
                Header: 'File',
            },
            {
                accessor: 'size',
                Header: 'Size (MB)',
            },
            {
                accessor: 'upload_datetime',
                Header: 'Upload Datetime',
            },
            {
                accessor: 'download',
                Header: 'Download',
                disableGlobalFilter: true,
                disableSortBy: true,
            },
            {
                accessor: 'delete',
                Header: 'Delete',
                disableGlobalFilter: true,
                disableSortBy: true,
            }
        ];
        AdminInterface.instance.getFiles().then((response) => {
            const data = [];
            response.data.forEach(file => {
                let file_location = file.s3_uri.split(`/${file.user_id}/`)[1] || '';
                data.push({
                    id: file.id,
                    user_id: file.user_id,
                    filename: file_location,
                    size: Math.round((file.file_size / (1024 * 1024)) * 10) / 10,
                    download: <CartTableButton type="button" onClick={() => handleDownload(file)}><DownloadIcon /></CartTableButton>,
                    upload_datetime: file.upload_datetime ? new Date(`${file.upload_datetime}z`).toLocaleString() : '',
                    delete: <CartTableButton type="button" onClick={() => handleDelete(file)}><DeleteIcon className="red-hover"/></CartTableButton>,
                });
            });

            setFilesList({ tableHeaderData: columns, tableRowsData: data });
        }).catch((error) => {
            if (error?.response?.status !== 404) {
                withReactContent(Swal).fire({
                    icon: 'error',
                    title: "Error",
                    html: error.response?.data?.detail,
                });
            }
            setFilesList({ tableHeaderData: columns, tableRowsData: [] });
        }).finally(() => {
            setShowLoader(false);
        });
    }

    useEffect(() => {
        if (reload === false) return;
        
        GetFiles();
        setReload(false);
    }, [reload]);

    return (
        <Card>
            <CardBody>
                <CardTitleWrap>
                    <CardTitle>Files List</CardTitle>
                    <CardSubhead>View list of files uploaded into AWS S3</CardSubhead>
                </CardTitleWrap>
                {
                    !showLoader &&
                    <ReactTableBase columns={filesList.tableHeaderData} data={filesList.tableRowsData} tableConfig={tableConfig} />
                }
                {showLoader && (<div className="text-center"><TableSpinner animation="border" /></div>)}
            </CardBody>
        </Card>
    );
};

export default FilesList;